import { type CSSProperties, type ReactNode } from "react";
import { Icon, Pill, T } from "@v0y4ge/jrni-ui";
import type { UIEvent } from "../../lib/events-adapter";
import { useT } from "../../lib/i18n";

/** EventCard + MetaRow — verbatim port (grid/list event card). */
function MetaRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.n2, lineHeight: 1.5 }}>
      <span style={{ color: T.p1, display: "inline-flex" }}>{icon}</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{children}</span>
    </li>
  );
}

export function EventCard({ ev, compact, wide, onClick }: { ev: UIEvent; compact?: boolean; wide?: boolean; onClick?: () => void }) {
  const t = useT();
  const isLive = ev.status === "live";
  const fillPct = ev.capacity > 0 ? Math.round((ev.checked / ev.capacity) * 100) : 0;
  return (
    <div onClick={onClick} style={{ padding: 16, borderRadius: 8, cursor: onClick ? "pointer" : "default", background: T.white, border: `1px solid ${T.border}`, boxShadow: T.shadowMd, display: "flex", gap: 16, alignItems: "stretch" }}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.n1 }}>{ev.title}</h3>
          {isLive ? <Pill tone="success" dot>{t("common.status.live", "Live")}</Pill> : null}
          {ev.status === "draft" ? <Pill>{t("common.status.draft", "Draft")}</Pill> : null}
          {ev.status === "canceled" ? <Pill tone="danger">{t("common.status.cancelled", "Cancelled")}</Pill> : null}
          {ev.waitlist > 0 ? <Pill tone="warn">{ev.waitlist} {t("events.card.waitlist", "waitlist")}</Pill> : null}
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: wide ? "row" : "column", flexWrap: wide ? "wrap" : "nowrap", gap: wide ? 16 : 4 }}>
          <MetaRow icon={<Icon.calendar size={13} />}>{ev.date} · {ev.time}</MetaRow>
          <MetaRow icon={<Icon.user size={13} />}>{ev.host}</MetaRow>
          <MetaRow icon={<Icon.loc size={13} />}>{ev.location}</MetaRow>
          <MetaRow icon={<Icon.users size={13} />}>{ev.registered}/{ev.capacity > 0 ? ev.capacity : "∞"} {t("events.card.registered", "registered")}</MetaRow>
        </ul>
        {compact ? (
          <div style={{ marginTop: 4 }}>
            <div style={{ height: 6, background: T.n5, borderRadius: 999, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.min(100, fillPct)}%`, background: isLive ? T.green1 : T.p1 }} />
            </div>
            <div style={{ fontSize: 11, color: T.soft, marginTop: 4 }}>{ev.checked} {t("events.card.checkedIn", "checked in ·")}{fillPct}{t("events.card.ofCapacity", "% of capacity")}</div>
          </div>
        ) : null}
      </div>
      <div style={{ flexShrink: 0, width: compact ? 88 : 56, height: compact ? "auto" : 56, borderRadius: 8, background: ev.heroImage ? `url(${ev.heroImage}) center/cover` : ev.hue, alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center", color: T.white, fontSize: 18, fontWeight: 700, overflow: "hidden" }}>
        {ev.heroImage ? null : ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}
      </div>
    </div>
  );
}

export const thStyle: CSSProperties = { padding: "10px 16px", textAlign: "left", fontWeight: 600, boxSizing: "content-box" };
export const tdStyle: CSSProperties = { padding: "12px 16px", verticalAlign: "middle", color: T.n2, boxSizing: "content-box" };
export const viewBtnStyle = (active: boolean): CSSProperties => ({ padding: "0 12px", height: 34, border: "none", cursor: "pointer", background: active ? T.n6 : T.white, color: T.n1, fontSize: 13, fontWeight: 500, borderRight: `1px solid ${T.border}`, fontFamily: T.font });
